import { useState, useSyncExternalStore } from 'react';
import { Download, Share, SquarePlus, X } from 'lucide-react';
import { Button, Modal, useToast } from '@/components/ui';
import { isIos, promptInstall, usePwa } from '@/lib/pwa';

/**
 * Installationshinweis für die App (PWA).
 *  - variant="menu":   Eintrag „App installieren“ im Benutzermenü (alle Geräte)
 *  - variant="banner": dezentes Banner auf Mobilgeräten (+ iOS-Anleitungsdialog)
 * Chromium-Browser: eigenes „beforeinstallprompt“. iOS/iPadOS (Safari): Anleitung „Teilen → Zum Home-Bildschirm“.
 */

const DISMISS_KEY = 'mv:install-dismissed';
const DISMISS_DAYS = 30;

// Kleiner globaler Zustand für den iOS-Anleitungsdialog (Menü schließt sich beim Klick)
let iosHelpOpen = false;
const helpListeners = new Set<() => void>();
function setIosHelp(open: boolean) {
  iosHelpOpen = open;
  helpListeners.forEach((l) => l());
}
function useIosHelp() {
  return useSyncExternalStore(
    (l) => {
      helpListeners.add(l);
      return () => helpListeners.delete(l);
    },
    () => iosHelpOpen,
  );
}

function wasDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    return at > 0 && Date.now() - at < DISMISS_DAYS * 86400_000;
  } catch {
    return false;
  }
}

function rememberDismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* Speicher nicht verfügbar – Banner erscheint beim nächsten Laden erneut */
  }
}

function useInstallState() {
  const { installPrompt, standalone } = usePwa();
  const ios = isIos();
  const canPrompt = !!installPrompt && !standalone;
  const iosHint = ios && !standalone;
  return { canPrompt, iosHint, available: canPrompt || iosHint };
}

function IosHelpDialog() {
  const open = useIosHelp();
  return (
    <Modal
      open={open}
      onClose={() => setIosHelp(false)}
      title="App auf dem iPhone/iPad installieren"
      size="sm"
      footer={<Button onClick={() => setIosHelp(false)}>Schließen</Button>}
    >
      <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-700">
        <li>
          Seite in <strong>Safari</strong> öffnen.
        </li>
        <li>
          Unten auf <Share className="inline h-4 w-4 align-text-bottom" aria-label="Teilen" /> <strong>Teilen</strong> tippen.
        </li>
        <li>
          <SquarePlus className="inline h-4 w-4 align-text-bottom" aria-hidden /> <strong>Zum Home-Bildschirm</strong> wählen und mit „Hinzufügen“ bestätigen.
        </li>
      </ol>
      <p className="mt-3 text-xs text-slate-500">Push-Benachrichtigungen sind auf iPhone/iPad (ab iOS 16.4) nur in der zum Home-Bildschirm hinzugefügten App möglich.</p>
    </Modal>
  );
}

export function InstallPrompt({ variant, onAction }: { variant: 'menu' | 'banner'; onAction?: () => void }) {
  const { canPrompt, iosHint, available } = useInstallState();
  const toast = useToast();
  const [hidden, setHidden] = useState(wasDismissed);

  const install = async () => {
    onAction?.();
    if (canPrompt) {
      const ok = await promptInstall();
      if (ok) toast.success('Die App wird installiert.');
    } else if (iosHint) {
      setIosHelp(true);
    }
  };

  if (variant === 'menu') {
    if (!available) return null;
    return (
      <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => void install()}>
        <Download className="h-4 w-4" /> App installieren
      </button>
    );
  }

  // Banner: nur auf kleinen Bildschirmen, dezent und ausblendbar
  return (
    <>
      <IosHelpDialog />
      {available && !hidden && (
        <div className="flex items-center gap-3 border-b border-brand-100 bg-brand-50 px-3 py-2 text-sm text-brand-900 sm:px-5 lg:hidden" role="region" aria-label="App installieren">
          <Download className="h-4 w-4 shrink-0" />
          <p className="min-w-0 flex-1">
            {canPrompt ? (
              'Mietverwaltung als App installieren – schneller Zugriff und Push-Benachrichtigungen.'
            ) : (
              <>
                Als App nutzen: <Share className="inline h-3.5 w-3.5 align-text-bottom" aria-label="Teilen" /> Teilen → „Zum Home-Bildschirm“.
              </>
            )}
          </p>
          {canPrompt ? (
            <Button size="xs" variant="primary" onClick={() => void install()}>
              Installieren
            </Button>
          ) : (
            <Button size="xs" onClick={() => setIosHelp(true)}>
              Anleitung
            </Button>
          )}
          <button
            type="button"
            className="rounded p-1 text-brand-700 hover:bg-brand-100"
            aria-label="Hinweis ausblenden"
            onClick={() => {
              rememberDismiss();
              setHidden(true);
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );
}
